import { IAutoplayInfo } from 'library/ts/interfaces/IAutoplayInfo';
import { ICapabilities } from 'library/ts/interfaces/utilities/capabilities/ICapabilities';
import { LogService } from 'library/ts/utilities/logger/LogService';

const logger = LogService.create( { prefixes: [ 'SmartTag:' ] } );

/**
 * @description Maximum time (ms) to wait for autoplay detection before falling back to defaults.
 * Prevents indefinite hangs when video.play() or onloadeddata never settles.
 */
const DETECTION_TIMEOUT_MS = 3000;

/**
 * @description Fallback result when detection times out.
 * Assumes muted-only autoplay — the most common policy across modern browsers
 * (Chrome MEI, Safari, Firefox all default to allowing muted autoplay).
 */
const TIMEOUT_DEFAULT: IAutoplayInfo = {
	supportsUnmutedAutoplay: false,
	supportsMutedAutoplay: true
};

/**
 * @description Blank MP4 used as test source for autoplay detection.
 * Base64-encoded to avoid network dependency.
 */
export const blankMP4Base64: string = 'data:video/mp4;base64,AAAAHGZ0eXBpc29tAAACAGlzb21pc28ybXA0MQAAAAhmcmVlAAAC721kYXQhEAUgpBv/w' +
	'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
	'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
	'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
	'AAAAAAAAAAAAAAAAAAAAAAAAA3pwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
	'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAcCEQBSCkG//AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
	'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
	'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
	'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADengAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
	'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAcAAAAsJtb292AAAAbG12aGQAA' +
	'AAAAAAAAAAAAAAAAAPoAAAALwABAAABAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAA' +
	'AAAAAAAAAAAAAAAAAADAAAB7HRyYWsAAABcdGtoZAAAAAMAAAAAAAAAAAAAAAIAAAAAAAAALwAAAAAAAAAAAAAAAQEAAAAAAQAAAAAAAAAAAAAAA' +
	'AAAAAEAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAACRlZHRzAAAAHGVsc3QAAAAAAAAAAQAAAC8AAAAAAAEAAAAAAWRtZGlhAAAAIG1kaGQAAAAA' +
	'AAAAAAAAAAAAAKxEAAAIAFXEAAAAAAAtaGRscgAAAAAAAAAAc291bgAAAAAAAAAAAAAAAFNvdW5kSGFuZGxlcgAAAAEPbWluZgAAABBzbWhkAAAAAAAAA' +
	'AAAAAAkZGluZgAAABxkcmVmAAAAAAAAAAEAAAAMdXJsIAAAAAEAAADTc3RibAAAAGdzdHNkAAAAAAAAAAEAAABXbXA0YQAAAAAAAAABAAAAAAAAAAAAAg' +
	'AQAAAAAKxEAAAAAAAzZXNkcwAAAAADgICAIgACAASAgIAUQBUAAAAAAfQAAAHz+QWAgIACEhAGgICAAQIAAAAYc3R0cwAAAAAAAAABAAAAAgAABAAAAA' +
	'Acc3RzYwAAAAAAAAABAAAAAQAAAAIAAAABAAAAHHN0c3oAAAAAAAAAAAAAAAIAAAFzAAABdAAAABRzdGNvAAAAAAAAAAEAAAAsAAAAYnVkdGEAAABabW' +
	'V0YQAAAAAAAAAhaGRscgAAAAAAAAAAbWRpcmFwcGwAAAAAAAAAAAAAAAAtaWxzdAAAACWpdG9vAAAAHWRhdGEAAAABAAAAAExhdmY1Ni40MC4xMDE=';

/**
 * @description Player-agnostic autoplay capability detection.
 * Preserved as a fallback for adapters that don't implement getAutoplayCapabilities().
 * Currently dormant — avia-js adapter handles detection via its own singleton.
 *
 * Detection strategy: try unmuted play() → if rejected → try muted play() → if rejected → both false.
 * Wrapped in a timeout to prevent indefinite hangs.
 *
 * @param {ICapabilities} capabilities - Browser/device capability info for legacy fallback paths
 * @returns {Promise<IAutoplayInfo>}
 */
export const checkAutoplay = ( capabilities: ICapabilities ): Promise<IAutoplayInfo> => {
	return new Promise(
		( resolve, reject ) => {
			let videoElement: HTMLVideoElement;
			let settled = false;
			const timeoutId: ReturnType<typeof setTimeout> = setTimeout( () => {
				logger.warn( 'checkAutoplay: detection timed out, using defaults' );
				settle( TIMEOUT_DEFAULT );
			}, DETECTION_TIMEOUT_MS );

			/**
			 * @description Resolves the detection promise exactly once.
			 * The settled flag guards against races between timeout, normal completion,
			 * and error paths — only the first to call settle/fail wins.
			 * @param {IAutoplayInfo} info
			 */
			const settle = ( info: IAutoplayInfo ): void => {
				if ( settled ) {
					return;
				}
				settled = true;
				clearTimeout( timeoutId );
				removeVideoElement();
				logger.log( 'checkAutoplay:', info );
				resolve( info );
			};

			/**
			 * @description Rejects the detection promise on unexpected errors (DOM exceptions, null refs).
			 * Ensures the caller gets an error instead of a silent hang.
			 * @param {unknown} error
			 */
			const fail = ( error: unknown ): void => {
				if ( settled ) {
					return;
				}
				settled = true;
				clearTimeout( timeoutId );
				removeVideoElement();
				logger.warn( 'checkAutoplay: detection failed:', String( error ) );
				reject( error );
			};

			/**
			 * @description Creates an off-screen video element for autoplay testing.
			 * playsinline is set on both muted and unmuted to avoid iOS rejecting
			 * play() due to inline playback restrictions rather than autoplay policy.
			 * @param {boolean} muted - Whether to create a muted test element
			 * @returns {HTMLVideoElement}
			 */
			const createVideoElement = ( muted: boolean ): HTMLVideoElement => {
				const el: HTMLVideoElement = document.createElement( 'video' );
				el.setAttribute( 'playsinline', '' );

				if ( muted ) {
					el.muted = true;
					el.setAttribute( 'muted', '' );
				}

				el.volume = 0.005;
				el.style.position = 'absolute';
				el.style.width = '1px';
				el.style.height = '1px';
				el.style.left = '-1000px';
				el.style.top = '-1000px';

				el.src = blankMP4Base64;
				document.body.appendChild( el );

				return el;
			};

			/**
			 * @description Cleans up the test video element.
			 * Checks parentNode before removeChild to avoid throwing if element was already detached.
			 */
			const removeVideoElement = (): void => {
				if ( videoElement ) {
					videoElement.onloadeddata = undefined;
					videoElement.pause();
					videoElement.removeAttribute( 'src' );
					if ( videoElement.parentNode ) {
						videoElement.parentNode.removeChild( videoElement );
					}
					videoElement = null;
				}
			};

			// Legacy browser helpers — retained for backward compatibility.
			// These version thresholds are always true for modern browsers but provide
			// correct behavior if this fallback ever runs on older platforms.

			/** @description Chrome >= 54 introduced autoplay with muted attribute */
			const isChromeAutoplayEligible = (): boolean => {
				return capabilities.getChromeVersion().majorVersion >= 54;
			};

			/** @description iOS 10+ with Safari or Chrome supports muted autoplay */
			const isAutoplayEligibleIos = (): boolean => {
				const ios10 = ( capabilities.iOS().ios === true && parseInt( capabilities.iOS().version.toString().split( '.' )[ 0 ] ) >= 10 );
				const isOkSafari = ( ios10 && capabilities.isSafari );
				const isOkChrome = ( ios10 && !isOkSafari && ( capabilities.isChromeIOS || capabilities.isChrome ) && isChromeAutoplayEligible() );
				return isOkSafari || isOkChrome;
			};

			/** @description Android Chrome >= 54 supports muted autoplay */
			const isAutoplayEligibleAndroid = (): boolean => {
				return capabilities.android && capabilities.isChrome && isChromeAutoplayEligible();
			};

			/** @description Safari 11.0.0–11.0.2 treats "Stop Media with Sound" same as "No Autoplay" */
			const isEarlySafari11 = (): boolean => {
				const isSafariDesktop = capabilities.isSafari && capabilities.isDesktop;
				const safariVersion = isSafariDesktop ? capabilities.safariVersion.version : null;
				const versionArray = safariVersion && safariVersion.split( '.' );
				const majMinMatch = versionArray ? ( parseInt( versionArray[ 0 ] ) === 11 ) && ( parseInt( versionArray[ 1 ] ) === 0 ) : null;
				if ( majMinMatch === null ) {
					return false;
				}
				return majMinMatch && ( !versionArray[ 2 ] || ( parseInt( versionArray[ 2 ] ) < 3 ) );
			};

			/** @description Heuristic for legacy mobile browsers where play() returns void instead of a Promise */
			const detectMobileCapabilities = (): void => {
				settle( {
					supportsUnmutedAutoplay: false,
					supportsMutedAutoplay: isAutoplayEligibleAndroid() || isAutoplayEligibleIos()
				} );
			};

			/**
			 * @description Second stage: only called when unmuted play() was rejected.
			 * Tests whether muted autoplay is permitted, with a short-circuit for early Safari 11
			 * which blocked muted autoplay entirely.
			 */
			const checkMutedAutoplay = (): void => {
				try {
					removeVideoElement();

					if ( isEarlySafari11() ) {
						settle( { supportsUnmutedAutoplay: false, supportsMutedAutoplay: false } );
						return;
					}

					videoElement = createVideoElement( true );
					videoElement.load();

					videoElement.play()
						.then( () => settle( { supportsUnmutedAutoplay: false, supportsMutedAutoplay: true } ) )
						.catch( () => settle( { supportsUnmutedAutoplay: false, supportsMutedAutoplay: false } ) );
				} catch ( error ) {
					fail( error );
				}
			};

			/**
			 * @description First stage: attempts unmuted play().
			 * If the Promise rejects (NotAllowedError), falls through to checkMutedAutoplay().
			 * If play() returns void (legacy browsers), uses heuristic-based detection instead.
			 */
			const checkUnmutedAutoplay = (): void => {
				try {
					videoElement = createVideoElement( false );

					videoElement.onloadeddata = (): void => {
						const promise = videoElement.play();

						// Legacy: browsers where play() doesn't return a Promise (IE11, pre-2016)
						if ( promise === undefined ) {
							if ( capabilities.isMobile.any() ) {
								detectMobileCapabilities();
							} else {
								settle( { supportsUnmutedAutoplay: true, supportsMutedAutoplay: true } );
							}
							return;
						}

						promise
							.then( () => settle( { supportsUnmutedAutoplay: true, supportsMutedAutoplay: true } ) )
							.catch( () => checkMutedAutoplay() );
					};

					videoElement.load();
				} catch ( error ) {
					fail( error );
				}
			};

			// Defers to DOMContentLoaded if body isn't available yet
			// (createVideoElement needs document.body.appendChild)
			try {
				if ( document.body !== null ) {
					checkUnmutedAutoplay();
				} else {
					document.addEventListener( 'DOMContentLoaded', () => checkUnmutedAutoplay() );
				}
			} catch ( error ) {
				fail( error );
			}
		}
	);
};